import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Sidebar, Menu, MenuItem } from "react-pro-sidebar";
import MenuRoundedIcon from "@mui/icons-material/MenuRounded";
import GridViewRoundedIcon from "@mui/icons-material/GridViewRounded";
import PhotoRoundedIcon from "@mui/icons-material/PhotoRounded";

export default function AdminSidebar() {
  const [collapsed, setCollapsed] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [username, setUsername] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const checkScreenSize = () => {
      const mobile = window.innerWidth < 992;
      setIsMobile(mobile);
      setCollapsed(mobile);
    };
    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  useEffect(() => {
    const fetchUsername = async () => {
      try {
        const res = await fetch('/api/getUsername');
        if (res.ok) {
          const data = await res.json();
          setUsername(data.username || '');
        } else {
          setUsername('');
        }
      } catch {   
        setUsername('');
      }
    };
    fetchUsername();
  }, []);

  const collapseSidebar = () => {
    if (isMobile) {
      setDrawerOpen(!drawerOpen);
    } else {
      setCollapsed(!collapsed);
    }
  };

  const closeDrawer = () => {
    if (isMobile) {
      setDrawerOpen(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('authToken');
    sessionStorage.clear();
    navigate('/login', { replace: true });
  };

  if (isMobile) {
    return (
      <>
        <nav className="navbar navbar-expand-lg navbar-dark bg-dark fixed-top">
          <div className="container-fluid">
            <Link className="navbar-brand fw-bold" to="/">
              KLV <span className="text-info">Diagnostic</span>
            </Link>
            <button className="btn btn-outline-light" onClick={collapseSidebar}>
              <MenuRoundedIcon />
            </button>
          </div>
        </nav>

        {drawerOpen && (
          <div
            className="position-fixed w-100 h-100 bg-dark bg-opacity-50"
            style={{ top: 0, left: 0, zIndex: 1040 }}
            onClick={closeDrawer}
          ></div>
        )}

        <div
          className={`position-fixed top-0 start-0 bg-white h-100 shadow-lg d-flex flex-column justify-content-between ${
            drawerOpen ? 'translate-x-0' : 'translate-x-n100'
          } transition-transform`}
          style={{
            width: '250px',
            zIndex: 1050,
            top: 0
          }}
        >
          <div
            className="p-3 border-bottom"
            style={{
              flexShrink: 0,
              backgroundColor: 'white',
              zIndex: 10,
              marginTop: 0,
              paddingTop: '1rem', 
            }}
          >
            <div className="d-flex align-items-center justify-content-between">
              <div>
                <h5 className="mb-1 fw-bold" style={{ margin: 0, lineHeight: 1 }}>
                  KLV <span className="text-primary">Diagnostic</span>
                </h5>
                <small className="text-muted">Welcome, {username || 'User'}</small>
              </div>
              <button className="btn btn-sm btn-outline-secondary" onClick={closeDrawer}>
                ✕
              </button>
            </div>
          </div>

          <nav className="py-3 flex-grow-1 overflow-auto">
            <ul className="nav flex-column">
              <li className="nav-item">
                <Link
                  to="/Component/Dashboard"
                  className="nav-link text-dark d-flex align-items-center px-3 py-3"
                  onClick={closeDrawer}
                >
                  <GridViewRoundedIcon className="me-3" />
                  <span>Dashboard</span>
                </Link>
              </li>
              <li className="nav-item">
                <Link
                  to="/Component/ImgUploadForm"
                  className="nav-link text-dark d-flex align-items-center px-3 py-3"
                  onClick={closeDrawer}
                >
                  <PhotoRoundedIcon className="me-3" />
                  <span>Manage Banner</span>
                </Link>
              </li>
              <li className="nav-item">
                <Link
                  to="/Component/ManagePackages"
                  className="nav-link text-dark d-flex align-items-center px-3 py-3"
                  onClick={closeDrawer}
                >
                  <PhotoRoundedIcon className="me-3" />
                  <span>Manage Packages</span>
                </Link>
              </li>
            </ul>
          </nav>

          <div className="p-3 border-top" style={{ flexShrink: 0 }}>
            <button className="btn btn-danger w-100" onClick={handleLogout}>
              Logout
            </button>
          </div>
        </div>

        <style jsx>{`
          .translate-x-n100 {
            transform: translateX(-100%);
          }
          .translate-x-0 {
            transform: translateX(0);
          }
          .transition-transform {
            transition: transform 0.3s ease-in-out;
          }
        `}</style>
      </>
    );
  }

  return (
    <Sidebar className="app custom-sidebar" collapsed={collapsed}>
      <Menu>
        <MenuItem icon={<MenuRoundedIcon onClick={collapseSidebar} />}>
          <div className="text-center" style={{ marginTop: '0' }}>
            <h6 style={{ marginTop: 0 }}>
              <strong>
                KLV<br />
                <span>Diagnostic</span>
              </strong>
            </h6>
          </div>
        </MenuItem>

        <MenuItem component={<Link to="/Component/Dashboard" className="link" />} icon={<GridViewRoundedIcon />}>
          Dashboard
        </MenuItem>
        <MenuItem component={<Link to="/Component/ImgUploadForm" className="link" />} icon={<PhotoRoundedIcon />}>
          Manage Banner
        </MenuItem>
        <MenuItem component={<Link to="/Component/ManagePackages" className="link" />} icon={<PhotoRoundedIcon />}>
          Manage Packages
        </MenuItem>
      </Menu>

      <style jsx>{`
        .custom-sidebar {
          position: fixed !important;
          top: 0;
          left: 0;
          width: 250px !important;
          height: 100vh !important;
          background: #ffffff;
          color: #000000;
          box-shadow: 2px 0 10px rgba(0, 0, 0, 0.15);
          z-index: 1000;
          font-family: 'Segoe UI', sans-serif;
        }

        .ps-menu-button {
          font-size: 16px;
          padding: 12px 20px;
          color: #000000 !important;
          transition: all 0.3s ease;
        }

        .ps-menu-button:hover {
          background-color: #f0f0f0 !important;
          color: #1e88e5 !important;
        }

        .ps-active {
          background-color: #1e88e5 !important;
          color: #ffffff !important;
        }

        .link {
          text-decoration: none;
          color: inherit;
        }
      `}</style>
    </Sidebar>
  );
}
